import { AppAuthProvider, ErrorBoundary } from 'auth';
import { Suspense, lazy } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { WelcomePopupController } from '@/modules/auth/components/WelcomePopupController.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import TaskForceLogin from '@/modules/common/pages/TaskForceLogin.tsx';
import TaskPublic from '@/modules/common/pages/TaskPublic.tsx';
import { commonPaths } from '@/modules/common/routes.tsx';
import { getDashboardRoutes } from '@/modules/admin-dashboard/routes.tsx';
import { getEmployeeDashboardRoutes } from '@/modules/employee-dashboard/routes.tsx';
import { ModalProvider, Spinner } from 'ui';
import RootRoute from '@/RootRoute.tsx';

const NotFound = lazy(() => import('./modules/common/pages/NotFound.tsx'));
const Task = lazy(() => import('./modules/common/pages/Task.tsx'));
const PublicDocument = lazy(() => import('./modules/common/pages/PublicDocument.tsx'));
const PublicFolder = lazy(() => import('./modules/common/pages/PublicFolder.tsx'));
const IncidentReporting = lazy(() => import('./modules/common/pages/IncidentReporting.tsx'));

export const AppRoutes = () => {
  const routerRoutes: RouteObject[] = [
    {
      path: '',
      element: (
        <AppAuthProvider site="compliance" preservedLocalStorageKeys={[BUSINESS_QUERY_PARAM]}>
          <ErrorBoundary homeUrl={commonPaths.static.root}>
            <ModalProvider>
              <WelcomePopupController />
              <Outlet />
            </ModalProvider>
          </ErrorBoundary>
        </AppAuthProvider>
      ),
      children: [
        {
          path: '/',
          element: <RootRoute />,
        },
        {
          path: '/login',
          element: <RootRoute />,
        },
        ...getDashboardRoutes(),
        ...getEmployeeDashboardRoutes(),
        {
          path: commonPaths.dynamic.task(),
          element: <Task />,
        },
        {
          path: commonPaths.static.taskPublic,
          element: <TaskPublic />,
        },
        {
          path: commonPaths.static.taskForceLogin,
          element: <TaskForceLogin />,
        },
        {
          path: commonPaths.dynamic.publicDocument(),
          element: <PublicDocument />,
        },
        {
          path: commonPaths.dynamic.publicFolder(),
          element: <PublicFolder />,
        },
        {
          path: commonPaths.static.incidentReporting,
          element: <IncidentReporting />,
        },
        {
          path: commonPaths.static.notFound,
          element: <NotFound />,
        },
        {
          path: '*',
          element: <Navigate to={commonPaths.static.notFound} replace />,
        },
      ],
    },
  ];

  const router = createBrowserRouter(routerRoutes);

  return (
    <Suspense
      fallback={
        <div className="flex h-[100dvh] w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      }
    >
      <RouterProvider router={router} />
    </Suspense>
  );
};
